/**
 * The HR module's shell (piece 34, Module shell and visibility; decision 1467).
 * 🔨 HR is a gate and not a Module (decision 0091): the group, its pages and
 * every route behind them are answered by `canUseCapability` on
 * `hr.employees.view`, which the seeded HR and Admin roles hold and no other
 * seeded role does. Neither `viewAllInModules` nor `editAllInModules` is read
 * on the way, so an override set holding Person reaches nothing here.
 */
import { canUseCapability, type GateContext } from '../permissions/guards';
import type { Viewer } from '../permissions/viewer';

/** 🔒 The key the HR navigation group, its pages and their routes are gated on (decision 1467). */
export const HR_EMPLOYEES_VIEW_CAPABILITY = 'hr.employees.view';
/**
 * 🔒 The key the policy text's reviewer holds (decision 1467; the owner,
 * 2026-09-30: *"make that a setting in user permissions"*). Seeded on HR and
 * Admin and granted in the role editor; nothing reads it until the discipline
 * batch's publish gate does.
 */
export const HR_POLICY_REVIEW_CAPABILITY = 'hr.policy.review';

/** The Employee list's own address, the HR group's first navigation row. */
export const HR_EMPLOYEES_ROUTE = '/hr/employees';
/** The route the browser asks for the list, relative to the API root. */
export const HR_EMPLOYEES_PATH = '/hr/employees';

/** The refusal every HR route answers a viewer without the key, in a sentence. */
export const HR_REFUSAL = 'The HR pages are open to the HR and Admin roles, and this identity holds neither.';

/** Whether the viewer reaches the HR group and its pages; the override sets are never read (decision 1467). */
export function canUseHrModule(viewer: Viewer, ctx: GateContext): boolean {
  return canUseCapability(viewer, HR_EMPLOYEES_VIEW_CAPABILITY, ctx);
}

/**
 * What the Employee list and Former Employees answer: the rows, and whether this viewer may hire (T2284). 🔒 `mayHire`
 * is `hr.hire`, more than the permission to view HR, so + Hire draws only for a holder of the key the hire asks.
 */
export interface EmployeeListAnswer {
  rows: EmployeeListRow[];
  mayHire?: boolean;
}

/**
 * One row of the Employee list: an active `EmploymentRecord` at the
 * Organization's own Company, with the position in force. 🔨 Derived, never an
 * `isEmployee` column (decision 0044), so a worker with no login is on it.
 */
export interface EmployeeListRow {
  employmentId: string;
  personId: string;
  name: string;
  title: string | null;
  department: string | null;
  /** The employment's start, as the calendar day it is stored. */
  startDate: string;
  /** The last day, on a Former Employees row (decision 3181). */
  endDate?: string | null;
}
